import { useMemo, useState } from 'react'
import { CircleAlert } from 'lucide-react'
import { Modal } from './Modal'
import { confirmMetropolitanBankBatch, type MetropolitanBatchReview } from '../lib/metropolitanData'
import { formatMoney } from '../lib/format'

export function MetropolitanBatchReviewModal({ batch, onClose, onConfirmed }: {
  batch: MetropolitanBatchReview
  onClose: () => void
  onConfirmed: () => void
}) {
  const [acknowledged, setAcknowledged] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState('')
  const totals = useMemo(() => batch.charges.reduce((result, charge) => {
    const amount = Number(charge.amount)
    const type = charge.snapshot.type
    result.general += amount
    if (type === 'BALAO') result.balloons += amount
    else result.installments += amount
    return result
  }, { installments: 0, balloons: 0, general: 0 }), [batch.charges])

  async function confirm() {
    setConfirming(true); setError('')
    try {
      await confirmMetropolitanBankBatch(batch.id)
      onConfirmed()
    } catch (caught) {
      setError(caught && typeof caught === 'object' && 'message' in caught && typeof caught.message === 'string' ? caught.message : 'Não foi possível confirmar o lote.')
    } finally { setConfirming(false) }
  }

  return <Modal title={`Revisão final do lote ${batch.id.slice(0, 8)}`} onClose={onClose} width="1100px">
    <div className="selection-summary batch-review-summary">
      <span><strong>{batch.charges.length}</strong> boletos</span>
      <span><strong>{batch.charges.filter(charge => charge.snapshot.type !== 'BALAO').length}</strong> parcelas</span>
      <span><strong>{batch.charges.filter(charge => charge.snapshot.type === 'BALAO').length}</strong> balões</span>
      <span>Parcelas <strong>{formatMoney(totals.installments)}</strong></span>
      <span>Balões <strong>{formatMoney(totals.balloons)}</strong></span>
      <span>Total <strong>{formatMoney(totals.general)}</strong></span>
    </div>
    <div className="data-card table-scroll"><table className="data-table"><thead><tr><th>Cliente</th><th>Unidade</th><th>Tipo</th><th>Competência</th><th>Valor</th><th>Vencimento</th><th>Código de controle</th><th>Descrição final</th></tr></thead><tbody>
      {batch.charges.map(charge => <tr key={charge.id}><td><strong>{charge.snapshot.customer_name || '—'}</strong></td><td>{charge.snapshot.unit || '—'}</td><td><span className={`status ${charge.snapshot.type === 'BALAO' ? 'pending' : 'active'}`}>{charge.snapshot.type === 'BALAO' ? 'Balão' : 'Parcela'}</span></td><td>{charge.snapshot.competence?.split('-').reverse().join('/') || '—'}</td><td className="money">{formatMoney(Number(charge.amount))}{charge.snapshot.amount_overridden && <small className="installment">Valor excepcional · padrão: {formatMoney(Number(charge.snapshot.schedule_amount))}</small>}</td><td>{charge.due_date.split('-').reverse().join('/')}</td><td><strong>{charge.control_code || '—'}</strong></td><td className="notes-cell">{charge.description || '—'}</td></tr>)}
    </tbody></table></div>
    <div className="info-banner batch-confirmation"><CircleAlert size={20}/><div><strong>Confirmação bancária</strong><span>Ao confirmar, este lote ficará disponível para o adaptador local. Cada parcela e balão continuará gerando uma cobrança separada.</span></div></div>
    <label className="batch-acknowledgement"><input type="checkbox" checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)}/><span>Revisei valores, vencimentos e descrições e autorizo o envio deste lote ao adaptador local.</span></label>
    {error && <p className="form-message">{error}</p>}
    <div className="form-actions"><button className="secondary" onClick={onClose}>Voltar e corrigir</button><button disabled={!acknowledged || confirming || !batch.charges.length} onClick={() => void confirm()}>{confirming ? 'Confirmando…' : 'Confirmar emissão pelo adaptador local'}</button></div>
  </Modal>
}
